import { CheckCircle2, ChevronRight, CircleHelp, Clock, ShieldCheck, XCircle } from "lucide-react";
import { formatTimeAgo } from "@/lib/ai-engine";
import { Chip } from "./chip";
import { blogCadenceOf, gateOf, usePublishPolicy } from "./agentic";
import { useMinWidth } from "./checks-panel";
import { fmtDateTime, isPublished, kindOf, type StudioItem } from "./helpers";
import {
  SCORE_BY_LABEL,
  awaitingQaOf,
  qaReviewOf,
  scoreCardOf,
  shortUrl,
  type FindingTone,
  type QaFinding,
  type QaReviewView,
  type Score,
  type ScoreCardView,
} from "./quality";

/**
 * Review view: "Score card" (the 10-item rubric, graded 0 to 3, each with its reason) and "Independent QA"
 * (a second reviewer's verdict, the claims it checked against their sources, and what it could not verify).
 */

// ── Pips ────────────────────────────────────────────────────────────────────

const PIP_ON: Record<Score, string> = { 0: "bg-red-500", 1: "bg-orange-400", 2: "bg-amber-300", 3: "bg-emerald-500" };
const SCORE_TEXT: Record<Score, string> = { 0: "text-red-300", 1: "text-orange-300", 2: "text-amber-200", 3: "text-emerald-300" };

/** Three pips: as many filled as the score. Always read together with the "2/3" next to them, so colour is never the only signal. */
export function Pips({ score }: { score: Score | null }) {
  return (
    <span className="inline-flex items-center gap-1" role="img" aria-label={score === null ? "Not graded yet" : `${score} out of 3`} data-testid="pips">
      {[1, 2, 3].map((n) => (
        <span
          key={n}
          className={`h-2.5 w-6 rounded-full ${
            score === null ? "border border-dashed border-muted-foreground/40" : n <= score ? PIP_ON[score] : "bg-muted-foreground/20"
          }`}
        />
      ))}
    </span>
  );
}

// ── Score card ──────────────────────────────────────────────────────────────

function scoreSummary(card: ScoreCardView, pending: boolean): string {
  if (card.scored === 0) return pending ? "Awaiting QA" : "Not scored";
  if (card.scored < card.rows.length) return `${card.scored} of ${card.rows.length} graded`;
  return `${card.sum} / ${card.max}`;
}

function ScoreCardPanel({ card, pending }: { card: ScoreCardView; pending: boolean }) {
  return (
    <section className="space-y-3" data-testid="panel-score-card">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 className="text-base font-semibold text-foreground">Score card</h3>
        <p className="text-sm font-semibold text-foreground" data-testid="score-total">{scoreSummary(card, pending)}</p>
      </div>
      <p className="text-xs text-muted-foreground">
        Each check is graded 0 to 3, where 3 is best. It is a grade, not a pass or fail, so a weak spot shows even when the piece passes.
        {card.weak > 0 && <span className="text-amber-300" data-testid="score-weak"> {card.weak} graded 1 or lower.</span>}
      </p>
      <ul className="grid gap-2 sm:grid-cols-2" data-testid="score-rows">
        {card.rows.map((r) => {
          const weak = r.score !== null && r.score <= 1;
          return (
            <li
              key={r.id}
              className={`rounded-xl border p-3 ${r.score === 0 ? "border-red-500/40 bg-red-500/[0.06]" : weak ? "border-amber-500/40 bg-amber-500/[0.06]" : "border-border bg-muted/20"}`}
              data-testid={`score-${r.id}`}
              data-score={r.score ?? "none"}
            >
              <div className="flex items-center justify-between gap-3">
                <p className="min-w-0 text-sm font-medium text-foreground" title={r.help || undefined}>{r.label}</p>
                <div className="flex shrink-0 items-center gap-2">
                  <Pips score={r.score} />
                  <span className={`w-8 text-right text-xs font-bold tabular-nums ${r.score === null ? "text-muted-foreground" : SCORE_TEXT[r.score]}`}>
                    {r.score === null ? "—" : `${r.score}/3`}
                  </span>
                </div>
              </div>
              <p className={`mt-1 break-words text-xs ${r.score === null ? "italic text-muted-foreground" : "text-foreground/75"}`} data-testid={`score-reason-${r.id}`}>
                {r.score === null ? (pending || r.by === "pending" ? "awaiting QA" : "not scored") : r.reason ?? "No reason recorded."}
              </p>
              {r.score !== null && r.by && r.by !== "pending" && (
                <p className="mt-1 text-[10px] text-muted-foreground/70" data-testid={`score-by-${r.id}`}>Graded by {SCORE_BY_LABEL[r.by]}</p>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ── Independent QA ──────────────────────────────────────────────────────────

const TONE_CHIP: Record<FindingTone, string> = {
  ok: "border-emerald-500/40 bg-emerald-500/15 text-emerald-200",
  warn: "border-amber-500/40 bg-amber-500/15 text-amber-200",
  bad: "border-red-500/40 bg-red-500/15 text-red-200",
  neutral: "border-border bg-muted/40 text-foreground/80",
};

function SourceCell({ f }: { f: QaFinding }) {
  if (!f.source) return <span className="text-muted-foreground">Not given</span>;
  if (f.sourceHref) {
    return (
      <a href={f.sourceHref} target="_blank" rel="noopener noreferrer nofollow" className="break-all text-blue-400 underline underline-offset-2" title={f.sourceHref}>
        {shortUrl(f.sourceHref)}
      </a>
    );
  }
  return <span className="break-words">{f.source}</span>;
}

function FindingsTable({ findings }: { findings: QaFinding[] }) {
  const wide = useMinWidth(640);
  if (!wide) {
    return (
      <ul className="space-y-2" data-testid="qa-findings" data-layout="cards">
        {findings.map((f, i) => (
          <li key={i} className="space-y-1.5 rounded-xl border border-border bg-muted/20 p-3 text-sm" data-testid={`qa-finding-${i}`} data-tone={f.tone}>
            <Chip className={TONE_CHIP[f.tone]}>{f.verdict}</Chip>
            <p className="break-words font-medium text-foreground">{f.claim}</p>
            <p className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground/80">Source: </span>
              <SourceCell f={f} />
            </p>
            {f.note && <p className="break-words text-xs text-muted-foreground">{f.note}</p>}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-border" data-testid="qa-findings" data-layout="table">
      <table className="w-full border-collapse text-left text-xs">
        <thead className="bg-muted/40 text-muted-foreground">
          <tr>
            <th className="w-[34%] px-3 py-2 font-medium">Claim</th>
            <th className="w-[24%] px-3 py-2 font-medium">Source</th>
            <th className="w-[14%] px-3 py-2 font-medium">Verdict</th>
            <th className="px-3 py-2 font-medium">Note</th>
          </tr>
        </thead>
        <tbody>
          {findings.map((f, i) => (
            <tr key={i} className="border-t border-border align-top" data-testid={`qa-finding-${i}`} data-tone={f.tone}>
              <td className="break-words px-3 py-2 text-sm text-foreground">{f.claim}</td>
              <td className="px-3 py-2"><SourceCell f={f} /></td>
              <td className="px-3 py-2"><Chip className={TONE_CHIP[f.tone]}>{f.verdict}</Chip></td>
              <td className="break-words px-3 py-2 text-muted-foreground">{f.note ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function countsLine(qa: QaReviewView): string {
  const parts = [
    qa.counts.ok ? `${qa.counts.ok} ok` : null,
    qa.counts.warn ? `${qa.counts.warn} unclear` : null,
    qa.counts.bad ? `${qa.counts.bad} ${qa.counts.bad === 1 ? "problem" : "problems"}` : null,
    qa.counts.neutral ? `${qa.counts.neutral} other` : null,
  ].filter(Boolean);
  return `${qa.findings.length} claim${qa.findings.length === 1 ? "" : "s"} checked${parts.length ? `: ${parts.join(", ")}` : ""}`;
}

function IndependentQaPanel({ qa, awaiting, article, requestedAt }: { qa: QaReviewView | null; awaiting: boolean; article: boolean; requestedAt: string | null }) {
  const { policy } = usePublishPolicy();
  const policyRequiresQa = !!policy && blogCadenceOf(policy).requireIndependentQa;
  return (
    <section className="space-y-3" data-testid="panel-independent-qa" data-state={awaiting ? "awaiting" : qa ? (qa.passed === false ? "failed" : qa.passed ? "passed" : "recorded") : "none"}>
      <h3 className="text-base font-semibold text-foreground">Independent QA</h3>
      <p className="text-xs text-muted-foreground">A second reviewer, working apart from the writer, reads the claims against the sources they cite.</p>

      {awaiting && (
        <div className="flex items-start gap-2 rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-sky-100" role="status" data-testid="qa-awaiting">
          <Clock size={15} className="mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium">Passed the checks — waiting for independent QA (usually under an hour)</p>
            <p className="text-xs text-sky-100/80">
              {requestedAt ? `Ordered ${formatTimeAgo(requestedAt)}. ` : ""}
              {policyRequiresQa ? "Your policy requires independent QA before anything publishes." : ""}
            </p>
          </div>
        </div>
      )}

      {qa ? (
        <div className="space-y-3">
          <p
            className={`flex items-center gap-2 text-sm font-semibold ${qa.passed === false ? "text-red-300" : qa.passed ? "text-emerald-300" : "text-foreground"}`}
            data-testid="qa-verdict-line"
          >
            {qa.passed === false ? <XCircle size={16} /> : qa.passed ? <CheckCircle2 size={16} /> : <CircleHelp size={16} />}
            {awaiting || qa.stale ? "Earlier review: " : ""}
            {qa.passed === false ? "Failed independent QA" : qa.passed ? "Passed independent QA" : "Independent QA recorded, no verdict"}
          </p>
          {qa.stale && !awaiting && (
            <p className="text-xs text-amber-300" data-testid="qa-stale">The draft changed after this review, so it needs a new one.</p>
          )}
          {(qa.by || qa.at) && (
            <p className="text-xs text-muted-foreground" data-testid="qa-meta">
              Reviewed{qa.by ? ` by ${qa.by}` : ""}
              {qa.at && fmtDateTime(qa.at) ? ` ${fmtDateTime(qa.at)}` : ""}
            </p>
          )}
          {qa.findings.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground" data-testid="qa-counts">{countsLine(qa)}</p>
              <FindingsTable findings={qa.findings} />
            </div>
          ) : (
            <p className="text-xs text-muted-foreground" data-testid="qa-no-findings">The reviewer recorded no claim-by-claim findings.</p>
          )}
          <div className="space-y-1.5" data-testid="qa-unverified">
            <div className="flex items-center gap-2">
              <CircleHelp size={14} className="shrink-0 text-amber-300" />
              <h4 className="text-sm font-semibold text-foreground">The reviewer could not verify</h4>
              {qa.unverified.length > 0 && <span className="rounded-full bg-amber-500/20 px-1.5 text-[11px] font-bold text-amber-300">{qa.unverified.length}</span>}
            </div>
            {qa.unverified.length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="qa-unverified-none">Nothing was left unverified.</p>
            ) : (
              <ul className="list-disc space-y-1 pl-5 text-sm text-foreground/90" data-testid="qa-unverified-list">
                {qa.unverified.map((u, i) => <li key={i} className="break-words">{u}</li>)}
              </ul>
            )}
          </div>
        </div>
      ) : (
        !awaiting && (
          <p className="text-xs text-muted-foreground" data-testid="qa-none">
            {article ? "Independent QA has not reviewed this piece, so no claim-by-claim check is on record." : "There is no independent QA record for this item."}
          </p>
        )
      )}
    </section>
  );
}

// ── The section ─────────────────────────────────────────────────────────────

/**
 * Sits between the decision header and the article. Collapsed unless something needs a look (waiting for QA, a failed review,
 * an unclear claim, a weak grade, or something the reviewer could not verify); the header row always carries the headline numbers.
 */
export function QualityReview({ item }: { item: StudioItem }) {
  const card = scoreCardOf(item);
  const qa = qaReviewOf(item);
  const awaiting = awaitingQaOf(item);
  const article = kindOf(item) === "article";
  const published = isPublished(item);
  // Articles in the pipeline always show it (so "awaiting QA" is visible); anything else only when it carries a grade or a review.
  if (!(card.present || qa || awaiting || (article && !published))) return null;

  const pending = awaiting || card.rows.some((r) => r.by === "pending");
  const attention = awaiting || qa?.passed === false || (qa?.counts.bad ?? 0) > 0 || (qa?.counts.warn ?? 0) > 0 || (qa?.unverified.length ?? 0) > 0 || card.weak > 0;
  const scoreTone = card.zeros > 0 ? "border-red-500/40 bg-red-500/15 text-red-200" : card.weak > 0 ? "border-amber-500/40 bg-amber-500/15 text-amber-200" : card.scored > 0 ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-200" : "border-border bg-muted/40 text-foreground/80";
  const qaChip = awaiting
    ? { cls: "border-sky-500/40 bg-sky-500/15 text-sky-200", text: "Independent QA: waiting" }
    : qa?.passed === false
      ? { cls: "border-red-500/40 bg-red-500/15 text-red-200", text: "Independent QA: failed" }
      : qa?.passed
        ? { cls: "border-emerald-500/40 bg-emerald-500/15 text-emerald-200", text: "Independent QA: passed" }
        : qa
          ? { cls: "border-border bg-muted/40 text-foreground/80", text: "Independent QA: no verdict" }
          : { cls: "border-border bg-muted/40 text-muted-foreground", text: "Independent QA: none yet" };

  return (
    <details open={attention} className="group rounded-2xl border border-border bg-card/70" data-testid="quality-review" data-attention={attention}>
      <summary className="flex cursor-pointer list-none flex-wrap items-center gap-x-2 gap-y-1.5 px-4 py-3 text-sm font-semibold text-foreground [&::-webkit-details-marker]:hidden">
        <ChevronRight size={16} className="shrink-0 text-muted-foreground transition-transform group-open:rotate-90" />
        <ShieldCheck size={15} className="shrink-0 text-muted-foreground" />
        Quality review
        <Chip className={scoreTone}>Score card: {scoreSummary(card, pending)}</Chip>
        <Chip className={qaChip.cls}>{qaChip.text}</Chip>
      </summary>
      <div className="space-y-6 border-t border-border p-4 sm:p-5">
        <ScoreCardPanel card={card} pending={pending} />
        <IndependentQaPanel qa={qa} awaiting={awaiting} article={article} requestedAt={awaiting ? gateOf(item)?.qaRequestedAt ?? null : null} />
      </div>
    </details>
  );
}
